import { App as AntdApp } from 'antd';
import { HelmetProvider } from 'react-helmet-async';
import { Router } from './router';
import { AuthProvider } from '@/context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { TenantProvider } from './context/TenantContext';
import { TourContextProvider } from '@/context/TourContext';
import { ChatProvider } from '@/context/ChatContext';
import { getTourStyles } from '@/tours/styles';
import { useTheme } from '@/hooks/useTheme';
import { TourProvider } from '@reactour/tour';
import type { StylesObj } from '@reactour/tour';
import useTenantBroadcast from './hooks/useTenantBroadcast';
import './styles/global.css';

/**
 * Custom close button for the guided tour — matches the blue/white theme.
 */
function TourCloseButton({ onClick }: { styles?: StylesObj; onClick?: () => void; disabled?: boolean }) {
  return (
    <button
      onClick={onClick}
      aria-label="Fechar tour"
      style={{
        position: 'absolute',
        top: 10,
        right: 10,
        width: 30,
        height: 30,
        borderRadius: '50%',
        backgroundColor: '#1f4bd8',
        border: 'none',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
        color: '#ffffff',
        flexShrink: 0,
      }}
    >
      <svg viewBox="0 0 9.1 9.1" width={12} height={12} aria-hidden>
        <path
          fill="currentColor"
          d="M5.9 4.5l2.8-2.8c.4-.4.4-1 0-1.4-.4-.4-1-.4-1.4 0L4.5 3.1 1.7.3C1.3-.1.7-.1.3.3c-.4.4-.4 1 0 1.4l2.8 2.8L.3 7.4c-.4.4-.4 1 0 1.4.2.2.4.3.7.3s.5-.1.7-.3L4.5 6l2.8 2.8c.3.2.5.3.8.3s.5-.1.7-.3c.4-.4.4-1 0-1.4L5.9 4.5z"
        />
      </svg>
    </button>
  );
}

/**
 * AppWithTenant — Sub-component that uses hooks requiring TenantProvider
 */
function AppWithTenant() {
  // Initialize cross-tab tenant synchronization
  useTenantBroadcast();
  const { preference } = useTheme();
  const isDark = preference.effectiveTheme === 'dark';

  return (
    <TourProvider steps={[]} styles={getTourStyles(isDark)} scrollSmooth components={{ Close: TourCloseButton }}>
      <TourContextProvider>
        <ChatProvider>
          <div className="app">
            <Router />
          </div>
        </ChatProvider>
      </TourContextProvider>
    </TourProvider>
  );
}

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <AntdApp>
          <AuthProvider>
            <TenantProvider>
              <AppWithTenant />
            </TenantProvider>
          </AuthProvider>
        </AntdApp>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
